import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useMaxWidth } from '../../../hooks/media-query';

import { Target } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { PageLayout } from '../../../components/ui/PageLayout';
import type { PageLayoutStat } from '../../../components/ui/PageLayout';
import GoalNode from './components/GoalNode';
import FilterPanel from './components/FilterPanel';
import GoalDetailModal from './components/GoalDetailModal';
import GoalListView from './components/GoalListView';
import MobileGoalTree from './components/MobileGoalTree';
import ManagedElsewhereNotice from '../../../components/capabilities/ManagedElsewhereNotice';
import { useCompanyCapabilities } from '../../../hooks/useCompanyCapabilities';

import type { RootState } from '../../../store/redux/store/store';
import type { Goal } from './types';
import { useGoals } from './useGoals';

// ----------------------
// COMPONENT
// ----------------------

const LEVEL_DEPTH: Record<Goal['level'], number> = { company: 1, team: 2, individual: 3 };
// Stands in for "no parent" so top-level nodes still form one sibling group.
const ROOT_KEY = '__root__';
// useGoals labels the top-level overflow tile with this sentinel; both mean "no parent".
const HOOK_ROOT_PARENT_KEY = 'root';
// Used for the very first layout, before the container has been measured.
const DEFAULT_CANVAS_WIDTH = 1100;

export default function GoalAlignmentMap() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const userId = useSelector((state: RootState) => {
    const user = state.user as Record<string, unknown>;
    return (user?.userId as string)
      || (user?.id as string)
      || ((user?.profile as Record<string, unknown>)?.sub as string)
      || (((user?.userAuthInfo as Record<string, unknown>)?.profile as Record<string, unknown>)?.sub as string)
      || localStorage.getItem('currentUserId')
      || '';
  });
  const isMobile = useMaxWidth(1199);

  const {
    // State
    isFilterPanelOpen,
    setIsFilterPanelOpen,
    selectedGoal,
    isModalOpen,
    setIsModalOpen,
    modalMode,
    hoveredGoal,
    setHoveredGoal,
    expandedSections,
    viewType,
    filters,
    expandedNodes,
    loading,
    detailLoading,
    savingGoal,
    error,
    successMessage,
    departments,
    loadingDepartments,
    availableOwners,
    loadingOwners,
    childLimits,

    // Computed
    mapGoals,
    allGoalsFlat,
    filtersActive,
    summaryStats,

    // Functions
    buildTreePositionMap,
    toggleNodeExpanded,
    showMoreChildren,
    applyStatusFilter,

    // Handlers
    handleGoalClick,
    openGoalById,
    handleCreateNewGoal: createNewGoal,
    handleSaveGoal,
    handleDeleteGoal,
    handleFilterChange,
    handleToggleSection,
    handleViewToggle,
    handleCloseError,
    handleCloseSuccess,
  } = useGoals(userId, t);

  // Gated on `loaded` so authoring buttons never appear and then vanish for a managed company.
  const { can, loaded: capabilitiesLoaded } = useCompanyCapabilities();
  const canAuthor = capabilitiesLoaded && can('canAuthorGoals');

  const handleCreateNewGoal = React.useCallback(() => {
    if (!canAuthor) return;
    createNewGoal();
  }, [canAuthor, createNewGoal]);

  const handleClearStatusFilter = React.useCallback(() => {
    handleFilterChange({ ...filters, status: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleClearFilters = React.useCallback(() => {
    handleFilterChange({ search: '', department: 'all', user: 'all', dueDate: 'all', status: [], levels: [] });
    // handleFilterChange is a plain setter on the hook and is stable enough for this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchParams] = useSearchParams();

  // ?goal=<id> opens that goal's detail modal. Equipment Coverage links here from a goal's
  // requirement group; the ref keeps a closed modal closed when something else re-renders.
  const deepLinkedGoalId = searchParams.get('goal');
  const consumedGoalDeepLink = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!deepLinkedGoalId || consumedGoalDeepLink.current === deepLinkedGoalId) return;
    consumedGoalDeepLink.current = deepLinkedGoalId;
    void openGoalById(deepLinkedGoalId);
  }, [deepLinkedGoalId, openGoalById]);

  // Auto-dismiss toasts
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(handleCloseError, 6000);
      return () => clearTimeout(timer);
    }
  }, [error, handleCloseError]);

  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(handleCloseSuccess, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, handleCloseSuccess]);

  const atRiskOnly = filters.status.length === 1 && filters.status[0] === 'at-risk';

  const summaryTiles: PageLayoutStat[] = React.useMemo(() => [
    { label: t('Total Goals'), value: summaryStats.total ?? 0 },
    { label: t('On Track'), value: summaryStats.onTrack ?? 0, color: 'text-primary' },
    {
      label: t('At Risk'),
      value: summaryStats.atRisk ?? 0,
      color: 'text-warning',
      // A second click clears the filter it set, so the tile is never a one-way door.
      onClick: () => (atRiskOnly ? handleClearStatusFilter() : applyStatusFilter('at-risk')),
      ariaLabel: atRiskOnly
        ? `${t('Clear filters')}: ${t('At Risk')}`
        : `${t('Filter by status')}: ${t('At Risk')}`,
    },
    { label: t('Completed'), value: summaryStats.completed ?? 0, color: 'text-success' },
    { label: t('Avg Progress'), value: `${(summaryStats.averageProgress ?? 0).toFixed(1)}%` },
  ], [summaryStats, applyStatusFilter, handleClearStatusFilter, atRiskOnly, t]);

  // A refetch keeps whatever is already on screen, so the only signal is this thin bar. The
  // track is always rendered so nothing shifts when it appears.
  const refreshing = loading && mapGoals.length > 0;
  const refreshBar = (
    <div className="mb-2 h-0.5 w-full overflow-hidden rounded-full">
      {refreshing && (
        <div
          role="status"
          aria-label={t('Loading...')}
          data-testid="goal-refresh-indicator"
          className="h-full w-full animate-pulse bg-primary/60"
        />
      )}
    </div>
  );

  // The five numbers, shared by the desktop overlay and the mobile strip. The badge says they
  // describe the filtered set rather than the whole company.
  const summaryBody = (
    <>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold text-foreground">{t('Summary')}</span>
        {filtersActive && (
          <span
            data-testid="goal-summary-filtered-note"
            className="rounded bg-muted px-1.5 py-0.5 text-[0.65rem] text-muted-foreground"
          >
            {t('Filtered')}
          </span>
        )}
      </div>
      <div className="grid grid-cols-5 gap-3">
        {summaryTiles.map((tile, index) => {
          const inner = (
            <>
              <div className={`text-sm font-bold ${tile.color ?? 'text-foreground'}`}>{tile.value}</div>
              <div className="text-[0.65rem] leading-tight text-muted-foreground">{tile.label}</div>
            </>
          );
          return tile.onClick ? (
            <button
              key={index}
              type="button"
              onClick={tile.onClick}
              aria-label={tile.ariaLabel ?? `${tile.value} ${tile.label}`}
              data-testid="goal-summary-stat"
              className="rounded px-1 text-center transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {inner}
            </button>
          ) : (
            <div key={index} className="px-1 text-center" data-testid="goal-summary-stat">
              {inner}
            </div>
          );
        })}
      </div>
    </>
  );

  // ----------------------
  // CANVAS MEASUREMENT
  // ----------------------

  // The canvas mounts and unmounts with the view toggle, so the element is held in state
  // rather than a ref and the observer follows it.
  const [canvasEl, setCanvasEl] = React.useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    if (!canvasEl) return;
    let frame = 0;
    const observer = new ResizeObserver((entries) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width > 0) setContainerWidth(width);
      });
    });
    observer.observe(canvasEl);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [canvasEl]);

  // The floating summary sits on the scroll container, not inside the scrolling content, so it can
  // never drift over a node. Its measured height is then reserved inside the layout so no node is
  // ever positioned beneath it in the first place.
  const [summaryEl, setSummaryEl] = React.useState<HTMLDivElement | null>(null);
  const [summaryHeight, setSummaryHeight] = React.useState(0);

  React.useEffect(() => {
    if (!summaryEl) {
      setSummaryHeight(0);
      return;
    }
    const read = () => setSummaryHeight(summaryEl.getBoundingClientRect().height);
    const observer = new ResizeObserver(read);
    observer.observe(summaryEl);
    read();
    return () => observer.disconnect();
  }, [summaryEl]);

  // The panel is inset 16px from the container top while the content origin sits 12px in, so its
  // bottom edge lands 4px into the content. The rest is clearance.
  const summaryInset = summaryHeight > 0 ? summaryHeight + 28 : 0;

  // Memoize tree layout computation
  const { posMap, showMoreTiles, maxX, maxY } = React.useMemo(() => {
    const layout = buildTreePositionMap(containerWidth || DEFAULT_CANVAS_WIDTH, summaryInset);
    let maxX = 0, maxY = 0;
    layout.posMap.forEach(pos => {
      maxX = Math.max(maxX, pos.x + pos.width + 40);
      maxY = Math.max(maxY, pos.y + pos.height + 40);
    });
    return { posMap: layout.posMap, showMoreTiles: layout.showMoreTiles, maxX, maxY };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildTreePositionMap, containerWidth, summaryInset, mapGoals, expandedNodes, childLimits, filters]);

  // ----------------------
  // KEYBOARD NAVIGATION
  // ----------------------

  // Ordering comes from the positions themselves, so the keyboard walks the tree in the
  // same order the eye reads it.
  const navNodes = React.useMemo(() => {
    const byId = new Map(mapGoals.map(g => [g.id, g]));
    const entries: { key: string; x: number; y: number; parentKey: string; level: number; hasChildren: boolean }[] = [];

    posMap.forEach((pos, key) => {
      const tile = showMoreTiles.get(key);
      if (tile) {
        const parent = byId.get(tile.parentId);
        entries.push({
          key,
          x: pos.x,
          y: pos.y,
          parentKey: tile.parentId === HOOK_ROOT_PARENT_KEY ? ROOT_KEY : tile.parentId,
          level: parent ? Math.min(LEVEL_DEPTH[parent.level] + 1, 3) : 1,
          hasChildren: false,
        });
        return;
      }
      const goal = byId.get(key);
      if (!goal) return;
      entries.push({
        key,
        x: pos.x,
        y: pos.y,
        parentKey: goal.parentId && posMap.has(goal.parentId) ? goal.parentId : ROOT_KEY,
        level: LEVEL_DEPTH[goal.level],
        hasChildren: mapGoals.some(g => g.parentId === goal.id),
      });
    });

    entries.sort((a, b) => (a.y - b.y) || (a.x - b.x));
    return entries;
  }, [posMap, showMoreTiles, mapGoals]);

  const navByKey = React.useMemo(
    () => new Map(navNodes.map(n => [n.key, n])),
    [navNodes],
  );

  const siblingGroups = React.useMemo(() => {
    const groups = new Map<string, string[]>();
    navNodes.forEach(node => {
      const list = groups.get(node.parentKey) ?? [];
      list.push(node.key);
      groups.set(node.parentKey, list);
    });
    return groups;
  }, [navNodes]);

  const [activeGoalId, setActiveGoalId] = React.useState<string | null>(null);
  const activeKey = activeGoalId && navByKey.has(activeGoalId) ? activeGoalId : (navNodes[0]?.key ?? null);

  const nodeRefs = React.useRef(new Map<string, HTMLButtonElement | null>());
  const registerNode = (key: string) => (el: HTMLButtonElement | null) => {
    if (el) nodeRefs.current.set(key, el);
    else nodeRefs.current.delete(key);
  };

  const focusNode = React.useCallback((key: string) => {
    setActiveGoalId(key);
    const el = nodeRefs.current.get(key);
    el?.focus();
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);

  const handleTreeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!activeKey || navNodes.length === 0) return;
    const current = navByKey.get(activeKey);
    if (!current) return;
    const siblings = siblingGroups.get(current.parentKey) ?? [];
    const index = siblings.indexOf(activeKey);

    // In Arabic the tree reads right to left, so the descend and ascend keys swap.
    let key = e.key;
    if (isRtl && key === 'ArrowRight') key = 'ArrowLeft';
    else if (isRtl && key === 'ArrowLeft') key = 'ArrowRight';

    switch (key) {
      case 'ArrowDown': {
        const next = siblings[index + 1];
        if (next) { e.preventDefault(); focusNode(next); }
        break;
      }
      case 'ArrowUp': {
        const previous = siblings[index - 1];
        if (previous) { e.preventDefault(); focusNode(previous); }
        break;
      }
      case 'ArrowRight': {
        if (!current.hasChildren) break;
        e.preventDefault();
        if (!expandedNodes.has(activeKey)) {
          toggleNodeExpanded(activeKey);
          break;
        }
        const firstChild = (siblingGroups.get(activeKey) ?? [])[0];
        if (firstChild) focusNode(firstChild);
        break;
      }
      case 'ArrowLeft': {
        if (current.hasChildren && expandedNodes.has(activeKey)) {
          e.preventDefault();
          toggleNodeExpanded(activeKey);
          break;
        }
        if (navByKey.has(current.parentKey)) {
          e.preventDefault();
          focusNode(current.parentKey);
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        focusNode(navNodes[0].key);
        break;
      }
      case 'End': {
        e.preventDefault();
        focusNode(navNodes[navNodes.length - 1].key);
        break;
      }
      default:
        break;
    }
  };

  // ----------------------
  // SCROLL AFFORDANCE
  // ----------------------

  const [scrollFade, setScrollFade] = React.useState({ start: false, end: false });

  const updateScrollFade = React.useCallback(() => {
    if (!canvasEl) return;
    const overflow = canvasEl.scrollWidth - canvasEl.clientWidth;
    // The canvas is always `dir="ltr"` (CO-816), so scrollLeft is the distance from the LEFT edge in
    // every locale. Math.abs stays as a guard in case a browser reports a negative offset.
    const travelled = Math.abs(canvasEl.scrollLeft);
    setScrollFade({
      start: overflow > 0 && travelled > 8,
      end: overflow > 0 && travelled < overflow - 8,
    });
  }, [canvasEl]);

  React.useEffect(() => {
    updateScrollFade();
  }, [updateScrollFade, maxX, containerWidth]);

  const treeIsEmpty = mapGoals.length === 0;
  // The company has nothing at all, as opposed to nothing matching the current filters.
  const noGoalsAtAll = allGoalsFlat.length === 0;

  const emptyState = (
    <Card variant="elevated">
      <CardContent className="p-8 text-center">
        <p className="text-muted-foreground">
          {noGoalsAtAll ? t('goals.map.empty') : t('goals.map.emptyFiltered')}
        </p>
        {noGoalsAtAll ? (
          canAuthor && (
            <Button onClick={handleCreateNewGoal} disabled={savingGoal} className="mt-4" data-testid="goal-map-empty-new-goal">
              <Icon name="Plus" size={16} />
              <span className="ms-2">{t('New Goal')}</span>
            </Button>
          )
        ) : (
          <Button
            variant="outline"
            onClick={handleClearFilters}
            className="mt-4"
            data-testid="goal-map-clear-filters"
          >
            {t('Clear filters')}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (isMobile) {
    return (
      <>
        <PageLayout
          title={t('Goal Alignment Map')}
          description={t('Align and track your individual, team, and company objectives')}
          icon={Target}
          accentColor="rose"
          maxWidth="1600px"
          headerActions={
            <div className="flex gap-1 w-full">
              <Button
                variant="outline"
                size="icon"
                onClick={handleViewToggle}
                iconName={viewType === 'map' ? 'List' : 'Map'}
                aria-label={viewType === 'map' ? t('List View') : t('Map View')}
              />
              {canAuthor && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCreateNewGoal}
                  disabled={savingGoal}
                  iconName="Plus"
                  aria-label={t('New Goal')}
                />
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                iconName="ListFilter"
                aria-label={t('Filters')}
                className={isFilterPanelOpen ? 'text-primary border-primary' : ''}
              />
            </div>
          }
        >
          <ManagedElsewhereNotice capability="canAuthorGoals" className="mb-4" />
          {refreshBar}

          {/* No canvas to float over on mobile, so the same summary sits inline above the tree. */}
          {viewType === 'map' && mapGoals.length > 0 && (
            <div
              data-testid="goal-summary-panel"
              className="mb-3 rounded-lg border border-border bg-card p-3 shadow-sm"
            >
              {summaryBody}
            </div>
          )}

          {loading && mapGoals.length === 0 ? (
              <div className="flex justify-center p-4">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              </div>
            ) : viewType === 'list' ? (
              <GoalListView
                filteredGoals={mapGoals}
                loading={loading && mapGoals.length === 0}
                filters={filters}
                handleGoalClick={handleGoalClick}
                t={t}
              />
            ) : (
              <MobileGoalTree
                goals={mapGoals}
                onGoalClick={handleGoalClick}
                expandedSections={expandedSections}
                onToggleSection={handleToggleSection}
              />
            )}

            {/* Filter Panel for mobile */}
            {isFilterPanelOpen && (
              <div className="fixed inset-0 z-[1300] flex justify-end bg-black/50">
                <div className="w-[85%] max-w-[400px] h-full bg-card overflow-auto">
                  <FilterPanel
                    isOpen={isFilterPanelOpen}
                    onToggle={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    isMobile={true}
                    allGoals={allGoalsFlat}
                    departments={departments}
                    loadingDepartments={loadingDepartments}
                  />
                </div>
              </div>
            )}
        </PageLayout>

        {/* Goal Detail Modal */}
        <GoalDetailModal
          goal={selectedGoal}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSaveGoal={handleSaveGoal}
          onDeleteGoal={handleDeleteGoal}
          allGoals={allGoalsFlat}
          mode={modalMode}
          detailLoading={detailLoading}
          departments={departments}
          loadingDepartments={loadingDepartments}
          availableOwners={availableOwners}
          loadingOwners={loadingOwners}
        />

        {/* Toast Notifications */}
        {error && (
          <div role="alert" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
            <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 shadow-lg">
              <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
              <span className="flex-1 text-sm">{error}</span>
              <button onClick={handleCloseError} aria-label={t('Close')} className="p-1 hover:bg-destructive/10 rounded flex-shrink-0">
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        )}
        {successMessage && (
          <div role="status" aria-live="polite" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
            <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success rounded-lg p-3 shadow-lg">
              <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
              <span className="flex-1 text-sm">{successMessage}</span>
              <button onClick={handleCloseSuccess} aria-label={t('Close')} className="p-1 hover:bg-success/10 rounded flex-shrink-0">
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <PageLayout
        title={t('Goal Alignment Map')}
        description={t('Align and track your individual, team, and company objectives')}
        icon={Target}
        accentColor="rose"
        maxWidth="1600px"
        headerActions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleViewToggle}
              data-testid="view-toggle-btn"
            >
              {viewType === 'map' ? <Icon name="List" size={16} /> : <Icon name="Map" size={16} />}
              <span className="ms-2">{viewType === 'map' ? t('List View') : t('Map View')}</span>
            </Button>
            {canAuthor && (
              <Button
                variant="outline"
                onClick={handleCreateNewGoal}
                disabled={loading || savingGoal}
                data-testid="new-goal-btn"
              >
                <Icon name="Plus" size={16} />
                <span className="ms-2">{t('New Goal')}</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              iconName="ListFilter"
              aria-label={t('Filters')}
              className={isFilterPanelOpen ? 'text-primary border-primary' : ''}
            />
          </div>
        }
      >
        <ManagedElsewhereNotice capability="canAuthorGoals" className="mb-4" />
        {refreshBar}

        {/* Main Content */}
          <div className="flex gap-3 min-w-0">
            {/* Filter Panel */}
            <div className={`w-[280px] flex-shrink-0 ${isFilterPanelOpen ? 'block' : 'hidden'}`}>
              <FilterPanel
                isOpen={isFilterPanelOpen}
                onToggle={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                filters={filters}
                onFilterChange={handleFilterChange}
                isMobile={false}
                allGoals={allGoalsFlat}
                departments={departments}
                loadingDepartments={loadingDepartments}
              />
            </div>

            {/* Conditional View Rendering */}
            <div className="flex-1 min-h-[70vh] min-w-0">
              {loading && mapGoals.length === 0 ? (
                <div className="flex justify-center items-center h-[50vh]">
                  <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                </div>
              ) : viewType === 'list' ? (
                <GoalListView
                  filteredGoals={mapGoals}
                  loading={loading && mapGoals.length === 0}
                  filters={filters}
                  handleGoalClick={handleGoalClick}
                  t={t}
                />
              ) : treeIsEmpty ? (
                emptyState
              ) : (
                <div className="relative h-full min-w-0">
                  <div
                    ref={setCanvasEl}
                    onScroll={updateScrollFade}
                    // CO-816 — every node below is positioned with a physical `left: position.x`
                    // computed by an LTR tree-layout algorithm (root at x=0, growing rightward).
                    // `overflow-auto` containers anchor their initial scroll position to the
                    // STARTING edge of `dir`, which for `dir="rtl"` (an Arabic session) is the
                    // right, not x=0 — so the map opened scrolled to the far end of the layout and
                    // the root/earlier nodes rendered clipped off-screen until the viewer scrolled
                    // manually. Forcing `dir="ltr"` on just this canvas keeps the coordinate system
                    // and the scroll anchor in agreement; per-title direction (Arabic titles reading
                    // right-to-left) is restored on the text itself via `dir="auto"` in GoalNode.
                    dir="ltr"
                    className="relative h-full min-w-0 max-w-full overflow-auto bg-background rounded-lg p-3"
                    data-testid="goal-alignment-map"
                  >
                    <div
                      role="tree"
                      aria-label={t('goals.map.region')}
                      tabIndex={-1}
                      onKeyDown={handleTreeKeyDown}
                      className="relative focus-visible:outline-none"
                      style={{ minWidth: maxX, minHeight: maxY }}
                    >
                        {/* Connection Lines */}
                        <svg aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, width: '100%', height: '100%' }}>
                          <defs>
                            <marker id="arrowhead-default" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                              <polygon points="0 0, 10 3.5, 0 7" fill="var(--color-border)" />
                            </marker>
                            <marker id="arrowhead-active" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                              <polygon points="0 0, 10 3.5, 0 7" fill="var(--color-primary)" />
                            </marker>
                          </defs>

                          {mapGoals.map((goal) => {
                            // Under an active filter the layout is flat level bands, not a tree
                            // (useGoals: buildTreePositionMap), so an edge would connect two nodes
                            // whose positions carry no parent/child meaning and would cut across
                            // unrelated bands. Show the nodes, drop the lines. (CO-524)
                            if (filtersActive) return null;
                            if (!goal.parentId) return null;
                            const parentPos = posMap.get(goal.parentId);
                            const childPos = posMap.get(goal.id);
                            if (!parentPos || !childPos) return null;
                            if (!expandedNodes.has(goal.parentId)) return null;

                            const startX = parentPos.x + parentPos.width / 2;
                            const startY = parentPos.y + parentPos.height;
                            const endX = childPos.x + childPos.width / 2;
                            const endY = childPos.y;
                            const midY = startY + (endY - startY) / 2;
                            const isActive = hoveredGoal === goal.id || hoveredGoal === goal.parentId;

                            return (
                              <path
                                key={`conn-${goal.parentId}-${goal.id}`}
                                d={`M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`}
                                fill="none"
                                stroke={isActive ? 'var(--color-primary)' : 'var(--color-border)'}
                                strokeWidth={isActive ? 2.5 : 1.5}
                                strokeOpacity={isActive ? 0.9 : 0.5}
                                strokeDasharray={isActive ? 'none' : '4 2'}
                                markerEnd={`url(#arrowhead-${isActive ? 'active' : 'default'})`}
                              />
                            );
                          })}
                        </svg>

                        {/* Goal Nodes with expand/collapse */}
                        {mapGoals.map((goal) => {
                          const position = posMap.get(goal.id);
                          if (!position) return null;
                          const nav = navByKey.get(goal.id);
                          const siblings = siblingGroups.get(nav?.parentKey ?? ROOT_KEY) ?? [];
                          const hasChildren = nav?.hasChildren ?? false;
                          const isExpanded = expandedNodes.has(goal.id);

                          return (
                            <div
                              key={goal.id}
                              data-testid={`goal-node-${goal.id}`}
                              // CO-816 review — the canvas is LTR for its coordinates only. Each node
                              // takes the language direction back, so in Arabic the status row, the
                              // progress fill and label, `me-2` and the chevron are RTL again. The
                              // physical `left: position.x` below does not depend on this `dir`.
                              dir={isRtl ? 'rtl' : 'ltr'}
                              className="absolute"
                              style={{
                                left: position.x,
                                top: position.y,
                                width: position.width,
                              }}
                              onMouseEnter={() => setHoveredGoal(goal.id)}
                              onMouseLeave={() => setHoveredGoal(null)}
                            >
                              <div className="relative">
                                <GoalNode
                                  goal={goal}
                                  level={goal.level}
                                  onNodeClick={() => handleGoalClick(goal)}
                                  tabIndex={goal.id === activeKey ? 0 : -1}
                                  nodeRef={registerNode(goal.id)}
                                  ariaProps={{
                                    role: 'treeitem',
                                    'aria-level': LEVEL_DEPTH[goal.level],
                                    'aria-expanded': hasChildren ? isExpanded : undefined,
                                    'aria-setsize': siblings.length,
                                    'aria-posinset': siblings.indexOf(goal.id) + 1,
                                  }}
                                  isConnected={hoveredGoal === goal.id ||
                                    mapGoals.some((g) => g.parentId === goal.id && hoveredGoal === g.id)}
                                />
                                {/* Expand/collapse toggle. Kept out of the tab order so the tree
                                    keeps a single tab stop; the arrow keys drive it. */}
                                {hasChildren && (
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    aria-expanded={isExpanded}
                                    aria-label={t(isExpanded ? 'goals.map.collapse' : 'goals.map.expand', { title: goal.title })}
                                    data-testid={`goal-expand-${goal.id}`}
                                    onClick={(e) => { e.stopPropagation(); toggleNodeExpanded(goal.id); }}
                                    className={`absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-card border-2 flex items-center justify-center cursor-pointer z-10 transition-all duration-200 hover:bg-primary/10 hover:border-primary ${
                                      isExpanded ? 'border-primary' : 'border-border'
                                    }`}
                                  >
                                    <Icon
                                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                                      size={14}
                                      color={isExpanded ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                                    />
                                  </button>
                                )}
                                {/* Owner badge */}
                                {goal.owner && goal.level !== 'company' && (
                                  <span
                                    className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[0.65rem] text-muted-foreground bg-card px-1 rounded"
                                    style={{
                                      bottom: hasChildren ? -28 : -16,
                                    }}
                                  >
                                    {goal.owner}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* Show-more tiles. Shaped like a node so they read as part of the tree. */}
                        {Array.from(showMoreTiles.entries()).map(([key, tile]) => {
                          const position = posMap.get(key);
                          if (!position) return null;
                          const nav = navByKey.get(key);
                          const siblings = siblingGroups.get(nav?.parentKey ?? ROOT_KEY) ?? [];

                          return (
                            <div
                              key={key}
                              dir={isRtl ? 'rtl' : 'ltr'}
                              className="absolute"
                              style={{
                                left: position.x,
                                top: position.y,
                                width: position.width,
                                height: position.height,
                              }}
                              data-testid={`goal-show-more-${tile.parentId}`}
                            >
                              <button
                                type="button"
                                role="treeitem"
                                aria-level={nav?.level ?? 1}
                                aria-setsize={siblings.length}
                                aria-posinset={siblings.indexOf(key) + 1}
                                ref={registerNode(key)}
                                tabIndex={key === activeKey ? 0 : -1}
                                onClick={() => showMoreChildren(tile.parentId)}
                                className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border bg-muted/40 px-3 text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                              >
                                <Icon name="Plus" size={16} />
                                <span>{t('goals.showMore', { count: tile.remaining })}</span>
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Pinned to the visible corner of the scroll container, so it stays put while
                      the canvas scrolls and never travels across a node. */}
                  <div
                    ref={setSummaryEl}
                    data-testid="goal-summary-panel"
                    className="absolute top-4 end-4 z-20 rounded-lg border border-border bg-card/95 p-3 shadow-sm backdrop-blur-sm"
                  >
                    {summaryBody}
                  </div>

                  {/* Edge fades: the horizontal scrollbar alone does not say there is more.
                      PHYSICAL left/right on purpose (CO-816 review): the canvas is `dir="ltr"` in every
                      locale, so "start" (content hidden before the scroll position) is always the left
                      edge. Logical start/end here flipped the fades onto the wrong edges in Arabic. */}
                  {scrollFade.start && (
                    <div aria-hidden="true" data-testid="goal-map-fade-start" className="pointer-events-none absolute inset-y-0 left-0 w-10 rounded-l-lg bg-gradient-to-r from-background to-transparent" />
                  )}
                  {scrollFade.end && (
                    <div aria-hidden="true" data-testid="goal-map-fade-end" className="pointer-events-none absolute inset-y-0 right-0 w-10 rounded-r-lg bg-gradient-to-l from-background to-transparent" />
                  )}
                </div>
              )}
            </div>
          </div>
      </PageLayout>

      {/* Goal Detail Modal */}
      <GoalDetailModal
        goal={selectedGoal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaveGoal={handleSaveGoal}
        onDeleteGoal={handleDeleteGoal}
        allGoals={allGoalsFlat}
        mode={modalMode}
        detailLoading={detailLoading}
        departments={departments}
        loadingDepartments={loadingDepartments}
        availableOwners={availableOwners}
        loadingOwners={loadingOwners}
      />

      {/* Toast Notifications */}
      {error && (
        <div role="alert" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
          <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 shadow-lg">
            <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
            <span className="flex-1 text-sm">{error}</span>
            <button onClick={handleCloseError} aria-label={t('Close')} className="p-1 hover:bg-destructive/10 rounded flex-shrink-0">
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
      {successMessage && (
        <div role="status" aria-live="polite" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1400] max-w-md w-full px-4">
          <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success rounded-lg p-3 shadow-lg">
            <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
            <span className="flex-1 text-sm">{successMessage}</span>
            <button onClick={handleCloseSuccess} aria-label={t('Close')} className="p-1 hover:bg-success/10 rounded flex-shrink-0">
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
